<script setup>

import { KNOLIA } from '@/knolia'
import { onMounted, onUpdated, ref, watch, defineProps } from 'vue'
import Activities from '../components/skill-card/Activities.vue'
import { Gems } from '../gems'
import router from "../router"
import { useUploadDocs } from '../composables/useUploadDocs'
import { MESSAGES } from "../messages"
import { useRatingSkills } from '../composables/useRatingSkills.js'

const { allowedExtensions, newFileName, DownloadFile, Upload, RemoveFile, RenameFile, SetVisibilityFile } = useUploadDocs()
const userRequestedId = ref(null)
const userData = ref({})
const expandedCriterias = ref({ activities: [], extraActivities: [], tasksWithoutParent: [], opSkillsWithoutParent: [], skills: [], attachments: [] })
const knowledges = ref([])
const defaultImage = ref(require('@/images/default.png'))
const showAll = ref(false)
const showAllSkills = ref(false)
const isLowerThanMobile = ref(KNOLIA.updateIsLowerThanMobile())
const isMobile = ref(KNOLIA.updateIsMobile())
const hideOnlyRatedKnowledges = ref(false)
const props = defineProps({
  userStore: {
    type: Object,
    default: Object
  }
})

const tabs = ref({
    functionAndActivities: true,
    knowledges: false,
    skillsGaps: false,
    attachments: false
})
const selfSkillCard = ref(false)
const ratingSkills = useRatingSkills()

async function ChangeUserPermission() {
    await KNOLIA.CallBackEnd("post", "set-operations&get=my-permission", {
        params: {
            user: props.userStore.User
        },
    })
}

function changeTab() {
    tabs.value.functionAndActivities = false
    tabs.value.knowledges = false
    tabs.value.skillsGaps = false
    tabs.value.attachments = false
    tabs.value.skills = false
}

async function GetSkillCard() {
    const input = document.getElementById('input')
    if(input)
        input.value = ""
    
    const inputCv = document.getElementById('input-cv')
    if(inputCv)
        inputCv.value = ""

    const responseSkillCard = await KNOLIA.CallBackEnd('get', `get-skill-card`)

    expandedCriterias.value.activities = responseSkillCard.data.activities
    expandedCriterias.value.extraActivities = responseSkillCard.data.extra_activities
    expandedCriterias.value.tasksWithoutParent = responseSkillCard.data.tasks_without_parent
    expandedCriterias.value.opSkillsWithoutParent = responseSkillCard.data.op_skills_without_parent
    expandedCriterias.value.skills_gaps = responseSkillCard.data.skills_gap
    expandedCriterias.value.skills = responseSkillCard.data.skills
    expandedCriterias.value.attachments = responseSkillCard.data.attachments.general
    expandedCriterias.value.cv = responseSkillCard.data.attachments.cv
    knowledges.value = responseSkillCard.data.knowledges
}

const generateGradient = (percent, color) => {
  return {
    background: `conic-gradient(${color} 0% ${percent}%, #10133D ${percent}% 100%)`
  };
};

async function InitSkillCard() {
    if(router.currentRoute.value.params.goToAttachments) {
        changeTab()
        tabs.value.attachments = true
    }

    selfSkillCard.value = KNOLIA.GetRequestedUserId() == KNOLIA.GetLoggedUserId()
    if(!selfSkillCard.value) {
        const response = await KNOLIA.CallBackEnd('get', `get-user`)
        
        response.data.user.accept_validate = Boolean(response.data.user.accept_validate) ?? false
        userData.value = response.data.user
    } else {
        userData.value = props.userStore.User
    }

    userRequestedId.value = KNOLIA.GetRequestedUserId()

    await GetSkillCard()
}

onMounted(async () => {
    await InitSkillCard()
})

onUpdated(async () => {
    if(userRequestedId.value != KNOLIA.GetRequestedUserId()) {
        await InitSkillCard()
    }
    const divTeams = document.getElementById('div-teams')

    if(userData.value.assigned_teams?.length > 0) {
        let tooltip = ''
        userData.value.assigned_teams.forEach(elem => tooltip += elem.designation + ', ')
        
        if(divTeams && userData.value.assigned_teams?.length > 1)
            Gems.Tooltip(divTeams, `Equipa(s): \r\n\r\n${tooltip.replaceAll(",", "\n").trim()}`, 'tooltip tooltip-left')
    }
})

watch(showAll, (e) => {
    expandedCriterias.value.activities.forEach(activity => {
        activity.show = e ? true : false
        
        activity.children.forEach(task => {
            task.show = e ? true : false
        })
    })
    expandedCriterias.value.extraActivities.forEach(activity => {
        activity.show = e ? true : false
        
        activity.children.forEach(task => {
            task.show = e ? true : false
        })
    })
})

watch(showAllSkills, (e) => {
    expandedCriterias.value.skills.forEach(skill => {
        skill.showChildren = e ? true : false
    })
})

window.addEventListener('resize', () => {
  isMobile.value = KNOLIA.updateIsMobile()
  isLowerThanMobile.value = KNOLIA.updateIsLowerThanMobile()
})

</script>


<template>
    <div>
        <div class="tab-container">
            <div class="tab" :class="{'tab-active': tabs.functionAndActivities}" @click="changeTab();tabs.functionAndActivities = true">
                <span>Função e Atividades</span>
            </div>
            <div class="tab" :class="{'tab-active': tabs.skills}" @click="changeTab();tabs.skills = true">
                <span>Competências</span>
            </div>
            <div class="tab" :class="{'tab-active': tabs.knowledges}" @click="changeTab();tabs.knowledges = true">
                <span>Conhecimentos</span>
            </div>
            <div class="tab" :class="{'tab-active': tabs.skillsGaps}" @click="changeTab();tabs.skillsGaps = true">
                <span>Gaps de Competência</span>
            </div>
            <div class="tab" :class="{'tab-active': tabs.attachments}" @click="changeTab();tabs.attachments = true">
                <span>Anexos</span>
            </div>
        </div>
        <div class="div-container">
            <div id="div-user-header">
                <img id="div-logo" class="img-user-photo" :src="userData.src ?? defaultImage" @error="$event.target.src = defaultImage">

                <div id="div-user-info">
                    <div id="div-name" class="line-clamp line-clamp-1 fw-bold">{{ userData.complete_name }}</div>
                    
                    <div class="line-clamp line-clamp-1">Nº Interno: <strong>{{ userData.user_internal }}</strong></div>

                    <div id="div-teams">
                        Equipa(s): 
                        <span class="fw-bold" v-if="userData.assigned_teams?.length == 0">[sem equipa]</span>
                        <span class="fw-bold" v-else-if="userData.assigned_teams?.length == 1" v-for="team in userData.assigned_teams" :key="team">{{ team.designation }}</span>
                        <span class="fw-bold" v-else>{{ userData.assigned_teams?.length }} equipas</span>
                    </div>

                    <div class="line-clamp line-clamp-1" v-if="userData.permissions?.can_validate && KNOLIA.GetRequestedUserId() == KNOLIA.GetLoggedUserId()">
                        <input type="checkbox" class="chk-validator cursor-pointer" v-model="userData.accept_validate" @change="ChangeUserPermission()">
                        <span>Aceito ser validador(a) <i class="fas fa-info-circle" @mouseenter="Gems.Tooltip($event.target, 'Aceito validar competências em que demonstro elevado domínio', 'tooltip')"></i></span>
                    </div>

                </div>

                <i class="fas fa-arrow-left go-back-icon cursor-pointer" @click="router.go(-1)" @mouseenter="Gems.Tooltip($event.target, 'Retroceder', 'tooltip')"></i>
            </div>
            
            <div id="div-separation-line"></div>
            
            <div v-if="tabs.functionAndActivities">
                <div class="div-activities-container div-job-title">
                    <div class="line-clamp line-clamp-1">{{ userData.user_job }}</div>
                    <div class="show-all-container cursor-pointer" @click="showAll = !showAll">
                        <span>Expandir/Fechar tudo</span>
                        <i class="icon fas" :class="{ 'fa-plus-square': !showAll, 'fa-minus-square': showAll }" />
                    </div>
                </div>

                <div v-if="expandedCriterias.activities.length > 0">
                    <Activities v-if="expandedCriterias.activities.length > 0" :expanded-criterias="expandedCriterias.activities" :is-activities-job="true" :mobile-view="isMobile || isLowerThanMobile">
                        <template #title>
                            <div class="div-activities-container">
                                <div>Atividades <i class="bi bi-arrow-right"></i> Tarefas <i class="bi bi-arrow-right"></i> Competências Operativas</div>
                            </div>
                        </template>
                    </Activities>
                </div>

                <div v-if="expandedCriterias.extraActivities.length > 0">
                    <Activities v-if="expandedCriterias.extraActivities.length > 0" :expanded-criterias="expandedCriterias.extraActivities" :is-activities-job="false" :mobile-view="isMobile || isLowerThanMobile">
                        <template #title>
                            <div class="div-activity-outside-job">
                                Atividades adicionadas do posto de trabalho
                            </div>
                        </template>
                    </Activities>
                </div>

                <div v-show="expandedCriterias.tasksWithoutParent.length > 0">
                    <div class="div-list-name">
                        Tarefas avulsas
                    </div>

                    <div class="div-element" v-for="task in expandedCriterias.tasksWithoutParent" :key="task.id">
                        {{ task.designation }}
                    </div>
                </div>
                
                <div v-show="expandedCriterias.opSkillsWithoutParent.length > 0">
                    <div class="div-list-name">
                        Competências operativas avulsas
                    </div>

                    <div class="div-element" v-for="opSkill in expandedCriterias.opSkillsWithoutParent" :key="opSkill.id">
                        {{ opSkill.designation }}
                    </div>
                </div>
            </div>

            <div v-if="tabs.skills">
                <!-- <div class="div-list-name">
                    Competências
                </div>  -->
                <div class="div-activities-container div-job-title">
                    <div class="line-clamp line-clamp-1">Competências</div>
                    <div class="show-all-container cursor-pointer" @click="showAllSkills = !showAllSkills">
                        <span>Expandir/Fechar tudo</span>
                        <i class="icon fas" :class="{ 'fa-plus-square': !showAllSkills, 'fa-minus-square': showAllSkills }" />
                    </div>
                </div>

                <div v-if="expandedCriterias.skills.length > 0">
                    <div v-for="skill in expandedCriterias.skills" :key="skill.id" >
                        <div class="div-element cursor-default justify-content-between">
                            <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                                <span @mouseenter="Gems.Tooltip($event.target, 'Competência', 'tooltip')">{{ skill.designation }}</span>
                            </div>
                            <i class="fas cursor-pointer skills" @click="skill.showChildren = !skill.showChildren"
                                :class="{ 'fa-chevron-down': !skill.showChildren, 'fa-chevron-up': skill.showChildren }"></i>
                        </div>
                            
                        <div class="div-op-skills-container" v-show="skill.showChildren">
                            <div class="div-skills-header fw-bold">
                                <span>Descritivo</span>
                                <span class="text-center">Proficiência</span>
                                <span class="text-center">Classificação</span>
                                <span class="text-center">Validação</span>
                            </div>
                            <div class="div-element div-skill div-skills cursor-default" v-for="op_skill in skill.children" :key="op_skill.id">
                                <div class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                                    <span @mouseenter="Gems.Tooltip($event.target, 'Competência operativa', 'tooltip')">{{ op_skill.designation }}</span>
                                </div>
                                <span class="text-center" @mouseenter="Gems.Tooltip($event.target, op_skill.proficiency_desig, 'tooltip')">{{ op_skill.proficiency ?? '--' }}</span>
                                <span class="text-center" @mouseenter="Gems.Tooltip($event.target, ratingSkills.tooltipSkillOp.value[op_skill.rating] ?? 'Sem classificação', 'tooltip')">
                                    {{ op_skill.rating ?? '--' }}
                                </span>
                                <span class="text-center" v-if="op_skill.validation_desig">{{ op_skill.validation_desig }}</span>
                                <span class="text-center fw-bold" v-else-if="op_skill.rating" @mouseenter="Gems.Tooltip($event.target, 'Validação não realizada', 'tooltip')">
                                    Declarado
                                </span>
                                <span class="text-center" v-else @mouseenter="Gems.Tooltip($event.target, 'Oriundo da função ou adicional ao posto de trabalho, mas não foi classificado', 'tooltip')">Atribuído</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="tabs.knowledges">
                <div class="div-list-name d-flex justify-content-between flex-wrap">
                    <span>Conhecimentos</span>
                    <span>
                        <label for="">Relacionados com a função/posto de trabalho</label>
                        <input class="input-knowledges cursor-pointer" type="checkbox" v-model="hideOnlyRatedKnowledges">
                    </span>
                </div>  
                
                <div>
                    <div v-if="!hideOnlyRatedKnowledges">
    
                        <div class="div-element div-knowledge cursor-default" v-for="knowledge in knowledges.all" :key="knowledge.id">
                            <span class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                                <span @mouseenter="Gems.Tooltip($event.target, knowledge.op_sk_list, 'tooltip tooltip-left')">{{ knowledge.designation }}</span>
                            </span>
                            <span class="text-center line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">{{ ratingSkills.tooltipKnowledge.value[knowledge.rating] ?? '--' }}</span>
                            <span class="text-center line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)" v-if="knowledge.validation">{{ knowledge.validation_desig }}</span>
                            <span class="text-center fw-bold line-clamp line-clamp-1" v-else @mouseenter="Gems.Tooltip($event.target, 'Validação não realizada', 'tooltip')">Declarado</span>
                        </div>

                    </div>

                    <div v-if="hideOnlyRatedKnowledges">
    
                        <div class="div-element div-knowledge cursor-default" v-for="knowledge in knowledges.only_work_related" :key="knowledge.id">
                            <span  class="line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">
                                <span @mouseenter="Gems.Tooltip($event.target, knowledge.op_sk_list, 'tooltip tooltip-left')">{{ knowledge.designation }}</span>
                            </span>
                            <span class="text-center line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)">{{ ratingSkills.tooltipKnowledge.value[knowledge.rating] ?? '--' }}</span>
                            <span class="text-center line-clamp line-clamp-1" @mouseenter="Gems.TooltipIfNeeded($event.target)" v-if="knowledge.validation">{{ knowledge.validation_desig }}</span>
                            <span class="text-center fw-bold line-clamp line-clamp-1" v-else @mouseenter="Gems.Tooltip($event.target, 'Validação não realizada', 'tooltip')">Declarado</span>
                        </div>
                    </div>
                </div>
            </div>


            <div v-if="tabs.skillsGaps">
                <div>
                    <div class="div-list-name div-skills-gap-title">
                        Gaps de Competência (para executar):
                    </div>

                    <div class="div-activities-container div-job-title">
                        <div class="line-clamp line-clamp-1">Às atividades/tarefas da função {{ userData.user_job ? userData.user_job : '[Sem função definida]' }}</div>
                    </div>
                    
                    <div class="div-skills-gap-list">
                        <div class="div-element div-skill-gap" v-if="expandedCriterias.skills_gaps.from_job.length == 0">
                            <span class="line-clamp line-clamp-1">Sem gaps de competência</span>
                        </div>

                        <div class="div-element div-skill-gap" v-for="gap in expandedCriterias.skills_gaps.from_job" :key="gap.id">
                            <span class="line-clamp line-clamp-1" @mouseenter="Gems.Tooltip($event.target, `${gap.task_designation}`, 'tooltip tooltip-left')">{{ gap.designation }}</span>
                            <div class="d-flex gap-4 align-items-center">
                                <div :style="generateGradient(gap.proficiency_percentage, '#3f739f')" class="pie-chart" @mouseenter="Gems.Tooltip($event.target, `Em falta: ${gap.gap_percentage}%`, 'tooltip')"></div>
                                <div @mouseover="gap.showFillNeedToImprove = true;Gems.Tooltip($event.target, gap.training_label, 'tooltip tooltip-left')" @mouseleave="gap.showFillNeedToImprove = false">
                                    <i v-if="gap.showFillNeedToImprove" class="bi bi-exclamation-square-fill icon-need-to-improve"></i>
                                    <i v-else class="bi bi-exclamation-square icon-need-to-improve"></i>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="div-activities-container div-job-title">
                        <div class="line-clamp line-clamp-1">Às atividades/tarefas adicionadas ao posto do trabalho</div>
                    </div>

                    <div class="div-skills-gap-list">
                        <div class="div-element div-skill-gap" v-if="expandedCriterias.skills_gaps.extra_job.length == 0">
                            <span class="line-clamp line-clamp-1">Sem gaps de competência</span>
                        </div>

                        <div class="div-element div-skill-gap" v-for="gap in expandedCriterias.skills_gaps.extra_job" :key="gap.id">
                            <span class="line-clamp line-clamp-1" @mouseenter="Gems.Tooltip($event.target, `${gap.task_designation}`, 'tooltip tooltip-left')">{{ gap.designation }}</span>
                            <div class="d-flex gap-4 align-items-center">
                                <div :style="generateGradient(gap.proficiency_percentage, '#3f739f')" class="pie-chart" @mouseenter="Gems.Tooltip($event.target, `Em falta: ${gap.gap_percentage}%`, 'tooltip')"></div>
                                <div @mouseover="gap.showFillNeedToImprove = true;Gems.Tooltip($event.target, gap.training_label, 'tooltip tooltip-left')" @mouseleave="gap.showFillNeedToImprove = false">
                                    <i v-if="gap.showFillNeedToImprove" class="bi bi-exclamation-square-fill icon-need-to-improve"></i>
                                    <i v-else class="bi bi-exclamation-square icon-need-to-improve"></i>
                                </div>
                            </div>
                        </div>
                    </div>



                    <div class="div-activities-container div-job-title">
                        <div class="line-clamp line-clamp-1">Às atividades/tarefas não ativadas</div>
                    </div>

                    <div class="div-skills-gap-list">
                        <div class="div-element div-skill-gap" v-if="expandedCriterias.skills_gaps.not_activated.length == 0">
                            <span class="line-clamp line-clamp-1">Sem gaps de competência</span>
                        </div>

                        <div class="div-element div-skill-gap" v-else v-for="gap in expandedCriterias.skills_gaps.not_activated" :key="gap.id">
                            <span class="line-clamp line-clamp-1" @mouseenter="Gems.Tooltip($event.target, `${gap.task_designation}`, 'tooltip tooltip-left')">{{ gap.designation }}</span>
                            <div class="d-flex gap-4 align-items-center">
                                <div :style="generateGradient(gap.proficiency_percentage, '#3f739f')" class="pie-chart" @mouseenter="Gems.Tooltip($event.target, `Em falta: ${gap.gap_percentage}%`, 'tooltip')"></div>
                                <div @mouseover="gap.showFillNeedToImprove = true;Gems.Tooltip($event.target, gap.training_label, 'tooltip tooltip-left')" @mouseleave="gap.showFillNeedToImprove = false">
                                    <i v-if="gap.showFillNeedToImprove" class="bi bi-exclamation-square-fill icon-need-to-improve"></i>
                                    <i v-else class="bi bi-exclamation-square icon-need-to-improve"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="tabs.attachments">
                <div class="div-list-name">
                    Anexos
                </div>

                <div v-if="expandedCriterias.cv" class="div-element div-file div-cv" :class="{'div-cv-self': selfSkillCard, 'div-cv-other': !selfSkillCard}">
                    <span class="ps-4">
                        <span class="cursor-pointer" @click="DownloadFile(expandedCriterias.cv)" @mouseenter="Gems.Tooltip($event.target, 'Download', 'tooltip')">Curriculum Vitae</span>
                    </span>
                    <span>{{ expandedCriterias.cv.uploaded_at }}</span>
                    <span class="span-icons" v-show="selfSkillCard">
                        <i class="fas fa-trash cursor-pointer" v-if="!expandedCriterias.cv.in_validation_process" @mouseenter="Gems.Tooltip($event.target, 'Remover', 'tooltip')" @click="RemoveFile(expandedCriterias.cv, () => {GetSkillCard()})"></i>
                        <i class="fas fa-trash unavailable" v-else @mouseenter="Gems.Tooltip($event.target, MESSAGES.TOOLTIPS.UNAVAILABLE_DURING_VALIDATION, 'tooltip')"></i>
                    </span>
                </div>

                <div v-else class="ps-2 div-cv-section">
                    <div v-if="selfSkillCard" class="cursor-pointer"  @mouseenter="Gems.Tooltip($event.target, 'Prima para adicionar o CV', 'tooltip')" onclick="document.getElementById('input-cv').click()">
                        CV não adicionado
                        <input class="hidden-input" id="input-cv" type="file" 
                            @change="Upload(expandedCriterias.attachments, $event.target.files, () => {GetSkillCard()}, true)" />
                    </div>
                    <div v-else>
                        CV não adicionado
                    </div>
                </div>

                <div>
                    <div class="div-attachments-row div-attachments-row-header" :class="{'div-attachments-row-self': selfSkillCard, 'div-attachments-row-other': !selfSkillCard}">
                        <span class="text-center" v-if="selfSkillCard">Público</span>
                        <span>Ficheiro</span>
                        <span>Importado em</span>
                        <input v-if="selfSkillCard" class="hidden-input" id="input" type="file" 
                            @change="Upload(expandedCriterias.attachments, $event.target.files, () => {GetSkillCard()}, false)" multiple />
                        <i v-if="selfSkillCard" class="bi bi-upload upload-icon" onclick="document.getElementById('input').click()" @mouseenter="Gems.Tooltip($event.target, allowedExtensions, 'tooltip')"></i>
                    </div>

                    <div v-if="expandedCriterias.attachments.length <= 0">
                        <div class="div-element">
                            <span>Nenhum anexo adicionado</span>
                        </div>
                    </div>
        
                    <div class="div-element div-file div-attachments-row" :class="{'div-attachments-row-self': selfSkillCard, 'div-attachments-row-other': !selfSkillCard}"
                        v-for="attachment in expandedCriterias.attachments" :key="attachment.id" >
                        <input v-if="selfSkillCard" type="checkbox" class="cursor-pointer" @mouseenter="Gems.Tooltip($event.target, 'Visível para utilizadores com permissões', 'tooltip')"
                            v-model="attachment.is_public" @change="SetVisibilityFile(attachment)"/>
                        <div class="file-name-row">
                            <span v-if="!attachment.rename" class="file-name cursor-pointer" @click="DownloadFile(attachment)" @mouseenter="Gems.Tooltip($event.target, 'Download', 'tooltip')">
                                {{ attachment.designation }}
                            </span>
                            <input class="input-rename ddl-alt-padding" type="text" v-model="newFileName" v-else-if="attachment.rename">
                        </div>
                        <span class="cursor-default">{{ attachment.uploaded_at }}</span>
                        <span class="span-icons" v-show="selfSkillCard">
                            <div class="div-icon-container">
                                <i class="fas fa-spell-check cursor-pointer" :class="{'selected-icon': attachment.rename}" v-if="!attachment.in_validation_process" @mouseenter="Gems.Tooltip($event.target, 'Renomear', 'tooltip')" @click="RenameFile(expandedCriterias.attachments, attachment, () => {GetSkillCard()})"></i>
                                <i class="fas fa-spell-check unavailable" v-else @mouseenter="Gems.Tooltip($event.target, MESSAGES.TOOLTIPS.UNAVAILABLE_DURING_VALIDATION, 'tooltip')"></i>
                            </div>
                            <div class="div-icon-container">
                                <i class="fas fa-trash cursor-pointer" v-if="!attachment.in_validation_process" @mouseenter="Gems.Tooltip($event.target, 'Remover', 'tooltip')" @click="RemoveFile(attachment, () => {GetSkillCard()})"></i>
                                <i class="fas fa-trash unavailable" v-else @mouseenter="Gems.Tooltip($event.target, MESSAGES.TOOLTIPS.UNAVAILABLE_DURING_VALIDATION, 'tooltip')"></i>
                            </div>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

.div-list-name {
    font-weight: bold;
    font-size: 1.2em;
    text-transform: uppercase;
    margin: 10px 0px;
}

.div-element {
    background-color: var(--default-app-alt-line-color);
    padding: 5px 15px 5px 15px;
    margin-top: 2px;
    display: flex;
    gap: 5px;
    align-items: center;
}

.div-knowledge, 
.div-skill-gap {
    justify-content: space-between;
}

.div-container {
    padding: 1%;
}

#div-teams {
    display: flex;
    gap: 5px;
    cursor: default;
}

.div-team + * {
    padding-left: 10px;
    border-left: 1px solid black;
}

#div-user-content {
    background-color: white;
    padding: 20px;
    width: 50%;
    box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
    border-radius: 5px;
}

#div-user-header {
    font-size: 1.3em;
    display: flex;
    align-items: center;
    gap: 1%;
    margin-bottom: 1%;
}

#div-separation-line {
    border-top: 1px solid;
    margin: 5px 0px;
}

#div-name {
    display: flex;
    justify-content: space-between;
}

.img-user-photo {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background-size: cover;
    background-position: center;
    object-fit: cover;
    margin: 15px;
}

.show-all-container {
    width: max-content;
}

.show-all-container i {
    margin-left: 10px;
}

.show-all-container:hover {
    color: var(--default-hover-color);
}

.div-activities-container {
    display: flex;
    justify-content: space-between;
    margin: 0px 0px 10px 0px;
}

.go-back-icon {
    font-size: 5rem;
    right: 0;
    position: absolute;
    margin-right: 40px;
}

.div-job-title {
    font-size: 1.2em;
    font-weight: bold;
    text-transform: uppercase;
    margin: 10px 0px;
}

.chk-validator {
    transform: scale(1.2);
    margin-right: 5px;
    margin-left: 2px;
}

.div-activity-outside-job {
    margin: 10px 0px;
}

.tab {
    display: flex;
    justify-content: center;
    text-transform: uppercase;
}

.tab-container {
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid #06071452;
}

.div-file:hover {
    color: var(--default-app-hover-color);
}

.file-name-row {
    display: flex;
    align-items: center;
    flex: 4;
    gap: 5px;
}

.div-skill-gap {
    cursor: default;
}

.pie-chart {
    width: 25px;
    height: 25px;
    border-radius: 50%;
    border: 1px solid;
}

/* UPLOAD SECTION */

.div-attachments-row-header {
    min-height: unset !important;
}

.div-attachments-row-self {
    grid-template-columns: 5% 73% 15% 7%
}

.div-attachments-row-other {
    grid-template-columns: 85% 15%;
    padding-left: 10px !important;
}

.div-attachments-row {
    display: grid;
    width: 100%;
    gap: 0px;
    padding: 5px 0px;
    min-height: 40px;
    align-items: center;
}

.hidden-input {
    display: none;
}

.select-files-btn {
    width: 100%;
}

.span-icons {
    display: flex;
    justify-content: center;
    gap: 10px;
    height: 20px;
    align-items: center;
}

.div-icon-container {
    width: 12px;
    width: 14px;
}

.input-rename {
    width: 95%;
}

.upload-icon {
    font-size: 1.5em;
    text-align: center;
    cursor: pointer;
}

.input-knowledges { 
    margin-left: 10px;
    transform: scale(1.2);
}

/* CV SECTION */

.div-cv-section {
    text-align: center;
    font-size: 1.2em;
    text-transform: uppercase;
    opacity: .5;
    padding: var(--default-app-padding);
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='10' ry='10' stroke='white' stroke-width='5' stroke-dasharray='6%2c 14' stroke-dashoffset='94' stroke-linecap='square'/%3e%3c/svg%3e");
    border-radius: 10px;
    margin-bottom: 23px;
}

.div-cv {
    display: grid;
    gap: 0px;
    padding: 5px 0px !important;
    margin-bottom: 30px;
    min-height: 40px;
}

.div-cv-self {
    grid-template-columns: 78% 15% 7%;
}

.div-cv-other {
    grid-template-columns: 85% 15%;
}

.unavailable {
    cursor: default;
    opacity: 0.5;
}

.div-op-skills-container {
    padding-left: 20px;
}

.div-skill {
    background-color: #2f4a63;
}

.div-skills-header, .div-skills {
    display: grid;
    grid-template-columns: 70% 10% 10% 10%;
    gap: 0px;
    padding: 5px 15px 5px 15px;
    margin-top: 2px;
    align-items: center;
}

.div-knowledge {
    display: grid;
    grid-template-columns: 70% 20% 10%;
}

.div-skills-gap-title {
    font-size: 1.4em;
}

.div-skills-gap-list {
    margin-bottom: 20px;
}

.icon-need-to-improve {
    display: flex;
    font-size: 1.7em;
}

@media screen and (max-width:1200px) {
    .div-attachments-row-self {
        grid-template-columns: 5% 63% 25% 7%
    }

    .div-cv-self {
        grid-template-columns: 68% 25% 7%;
    }

    .div-cv-other,
    .div-attachments-row-other {
        grid-template-columns: 75% 25%;
    }
}

@media screen and (max-width: 768px) {

    .div-container {
        padding: 0%;
    }

    #div-separation-line {
        margin: 0px 2%;
    }

    #div-user-header {
        font-size: 1.1em;
        gap: 2%;
        margin-bottom: 0%;
        padding: 2%;        
    }

    .div-activities-container {
        padding: 2% 2% calc(2% - 1px) 2%;
    }

    .img-user-photo {
        width: 65px;
        height: 65px;
    }

    .div-list-name,
    .div-activity-outside-job {
        margin: 2%;
    }

    .tab-container {
      width: 100%;
    }

    .div-attachments-row-self {
        grid-template-columns: 10% 59% 25% 5%;
    }
}

</style>
